import { useEffect, useState } from "react";
import { useAppLock, biometricAvailable } from "../contexts/AppLockContext";
import { useAuth } from "../contexts/AuthContext";
import { Lock, Fingerprint, LogOut, Delete, AlertTriangle } from "lucide-react";

/**
 * Full-screen overlay shown when App Lock is active. Blocks all pointer
 * events on the underlying app, hides sensitive content, offers PIN
 * entry, biometric unlock (when supported and registered) and a full
 * logout escape hatch.
 */
export default function AppLockScreen() {
    const { config, locked, verifyPin, biometricUnlock } = useAppLock();
    const { logout } = useAuth();
    const [pin, setPin] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    // Blur the underlying document while locked to hide sensitive info
    // from the OS app-switcher preview.
    useEffect(() => {
        if (locked) document.body.classList.add("vu-app-locked");
        else document.body.classList.remove("vu-app-locked");
        return () => document.body.classList.remove("vu-app-locked");
    }, [locked]);

    // Auto-try biometric on mount when enabled and supported.
    useEffect(() => {
        if (!locked || !config?.biometric_enabled || !biometricAvailable()) return;
        (async () => {
            try { await biometricUnlock(); } catch { /* user can retry manually */ }
        })();
    }, [locked, config?.biometric_enabled]);

    if (!locked || !config?.enabled) return null;

    const submit = async (fullPin) => {
        if (busy) return;
        setBusy(true);
        setErr("");
        try {
            await verifyPin(fullPin);
            setPin("");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = detail && typeof detail === "object" ? detail.code : null;
            if (code === "app_lock_forced_logout") {
                setErr(detail.message);
                setTimeout(() => logout(), 1500);
            } else if (code === "app_lock_wrong_pin") {
                const rem = Number(detail.remaining);
                setErr(rem > 0
                    ? `Incorrect PIN. ${rem} ${rem === 1 ? "attempt" : "attempts"} remaining.`
                    : detail.message);
            } else {
                setErr(typeof detail === "string" ? detail : "Incorrect PIN");
            }
            setPin("");
        } finally {
            setBusy(false);
        }
    };

    const press = (d) => {
        if (busy) return;
        const next = (pin + d).slice(0, 6);
        setPin(next);
        // Auto-submit at 6 digits; 4 or 5 digit PINs require explicit OK.
        if (next.length === 6) submit(next);
    };

    const del = () => setPin((p) => p.slice(0, -1));

    return (
        <div
            className="fixed inset-0 z-[9999] bg-gradient-to-br from-[#1a1318] via-[#2a1a24] to-[#1a1318] text-white flex flex-col items-center justify-center px-6"
            data-testid="app-lock-screen"
            role="dialog"
            aria-modal="true"
            aria-label="App is locked"
        >
            <div className="w-14 h-14 rounded-2xl bg-white/10 grid place-items-center mb-4">
                <Lock size={26} />
            </div>
            <h1 className="font-serif text-2xl mb-1" data-testid="app-lock-title">Locked</h1>
            <p className="text-white/70 text-sm mb-8">Enter your PIN to unlock</p>

            {/* PIN indicator */}
            <div className="flex gap-2 mb-6" data-testid="app-lock-pin-indicator">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div
                        key={i}
                        className={`w-3.5 h-3.5 rounded-full border transition ${
                            i < pin.length
                                ? "bg-white border-white"
                                : "bg-transparent border-white/40"
                        }`}
                    />
                ))}
            </div>

            {err && (
                <div className="mb-4 text-sm text-red-300 flex items-center gap-2" data-testid="app-lock-error">
                    <AlertTriangle size={14} /> {err}
                </div>
            )}

            <div className="grid grid-cols-3 gap-3 w-full max-w-[280px]" data-testid="app-lock-keypad">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                    <PadButton key={d} onClick={() => press(d)} disabled={busy} testid={`app-lock-key-${d}`}>{d}</PadButton>
                ))}
                <PadButton onClick={() => submit(pin)} disabled={busy || pin.length < 4} testid="app-lock-key-submit">
                    <span className="text-xs">OK</span>
                </PadButton>
                <PadButton onClick={() => press("0")} disabled={busy} testid="app-lock-key-0">0</PadButton>
                <PadButton onClick={del} disabled={busy} testid="app-lock-key-del">
                    <Delete size={20} />
                </PadButton>
            </div>

            <div className="mt-8 flex flex-col items-center gap-3">
                {config.biometric_enabled && biometricAvailable() && (
                    <button
                        type="button"
                        onClick={async () => {
                            try { await biometricUnlock(); }
                            catch (e) { setErr("Biometric unlock failed — use PIN"); }
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-sm"
                        data-testid="app-lock-biometric-btn"
                    >
                        <Fingerprint size={16} /> Use device unlock
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => logout()}
                    className="flex items-center gap-2 text-white/60 hover:text-white text-xs"
                    data-testid="app-lock-logout-btn"
                >
                    <LogOut size={12} /> Sign out
                </button>
            </div>
        </div>
    );
}

function PadButton({ children, onClick, disabled, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className="h-14 rounded-2xl bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-40 text-xl font-light transition"
        >
            {children}
        </button>
    );
}
